import classNames from 'classnames'

import { Image, Link, Skeleton } from '@src/quince-components/src'
import Swiper, { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { IContentfulCollection } from '../../../types/ContentfulCollectionService.types'
import { getFormattedSlug } from '../../../utils'
import { LinkGroupProps } from '../SubCategoryList'

import styles from './imageSubCategoryList.module.scss'

type TextSubCategoryListProps = LinkGroupProps & {
  sendEventOnButtonClick: (filterUsed: string, buttons: IContentfulCollection[]) => void
  className?: string
  contentful_id: string
  isNewCLPFiltersExperimentActive: boolean
  showLoader?: boolean
}

function ImageSubCategoryList({
  buttons = [],
  className,
  sendEventOnButtonClick,
  contentful_id,
  isNewCLPFiltersExperimentActive,
  showLoader,
}: TextSubCategoryListProps) {
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const getImageUrl = (button: IContentfulCollection) => {
    const imageUrl = button.thumbnailImage?.file.url ?? ''
    if (isNewCLPFiltersExperimentActive) return button.collectionExploreEverythingImage?.file.url ?? imageUrl
    return imageUrl
  }

  const getImageAltText = (button: IContentfulCollection) => {
    if (isNewCLPFiltersExperimentActive) return button.collectionExploreEverythingImage?.description ?? ''
    return button.thumbnailImage?.description ?? ''
  }

  if (showLoader && isDesktop) {
    return (
      <div className={styles.imageSubCategorySkeletonWrapper}>
        {new Array(9).fill(0).map((_, index) => (
          <div key={index} className={styles.imageSubCategorySkeletonItem}>
            <Skeleton className={styles.imageSubCategorySkeletonImage} />
            <Skeleton className={styles.imageSubCategorySkeletonTitle} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className={classNames(styles.wrapper, isNewCLPFiltersExperimentActive && styles.newCLPFiltersWrapper, className)}
    >
      <Swiper
        showDots={false}
        showNavigator={isDesktop}
        numberOfVisibleSlides={9}
        variableWidth
        id={`imageWithProductSlider-${contentful_id}`}
        isChevronIcon
        navPosition={NAV_POSITION.CENTER}
        prevIconClass={styles.prevArrowClass}
        nextIconClass={styles.nextArrowClass}
        arrowWrapperClass={styles.arrowWrapperClass}
        slideClassName={styles.textSubCategorySlide}
        className={styles.textSubCategorySwiper}
        preventInteractionOnTransition={true}
        isHorizontalCacheEnabled={true}
        key={`imageWithProductSlider-${contentful_id}`}
      >
        {buttons.map((button, index) => {
          const active = index === 0
          return (
            <Link
              key={button.id}
              className={classNames(
                styles.subCategoryItem,
                active && styles.active,
                active && isNewCLPFiltersExperimentActive && styles.newCLPFiltersActive,
              )}
              href={active ? undefined : '/' + getFormattedSlug(button.slug)}
            >
              <Image
                alt={getImageAltText(button)}
                url={getImageUrl(button)}
                title={button.name || ''}
                className={classNames(styles.imageWrapper, isNewCLPFiltersExperimentActive && styles.circularImage)}
                loading="eager"
              />
              <div
                className={classNames(
                  styles.titleWrapper,
                  isNewCLPFiltersExperimentActive && styles.newCLPFiltersTitleWrapper,
                )}
                data-content-type="category-title"
                data-href={button.slug}
                data-active={active}
              >
                <span
                  onClick={() => {
                    sendEventOnButtonClick(button.name || '', buttons)
                  }}
                  className={styles.imageTitle}
                >
                  {button.name}
                </span>
              </div>
            </Link>
          )
        })}
      </Swiper>
    </div>
  )
}

export default ImageSubCategoryList
